/**
 * R2AS upstairs TV — World Cup highlights + occluded proximity audio.
 * Blueprint: docs/R2AS_TV_WORLD_CUP.md
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Html } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import {
  R2AS_TV_AUDIO,
  R2AS_TV_MATCH,
  R2AS_TV_PLAYLIST,
  R2AS_TV_SCREEN,
  buildTvEmbedUrl,
  tvHearingVolume,
} from '../../data/r2asTv';

/** CSS pixel size of the iframe; mapped to world meters via distanceFactor. */
const CSS_W = 1280;
const CSS_H = 720;
const IFRAME_ID = 'r2as-tv-yt';

const _tvWorld = new THREE.Vector3();
const _origin = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _raycaster = new THREE.Raycaster();

/**
 * @param {{
 *   enabled?: boolean,
 *   pose?: typeof R2AS_TV_SCREEN,
 *   playlist?: typeof R2AS_TV_PLAYLIST,
 *   audio?: typeof R2AS_TV_AUDIO,
 * }} props
 */
export default function LabTvScreen({
  enabled = true,
  pose = R2AS_TV_SCREEN,
  playlist = R2AS_TV_PLAYLIST,
  audio = R2AS_TV_AUDIO,
}) {
  const { camera, scene } = useThree();
  const embedUrl = useMemo(() => buildTvEmbedUrl(playlist), [playlist]);
  const distanceFactor = (pose.width * 400) / CSS_W;

  const playerRef = useRef(null);
  const unlockedRef = useRef(false);
  const lastVolRef = useRef(-1);
  const occludedRef = useRef(false);
  const frameRef = useRef(0);
  const [unlocked, setUnlocked] = useState(false);
  /** Html is a DOM overlay (no depth test) — hide it when geometry blocks LOS. */
  const [hidden, setHidden] = useState(false);

  // Load YT IFrame API + bind player once iframe is in the DOM
  useEffect(() => {
    if (!enabled || !embedUrl) return undefined;
    let cancelled = false;
    let player = null;

    const bind = () => {
      if (cancelled || !window.YT?.Player) return;
      const el = document.getElementById(IFRAME_ID);
      if (!el) {
        requestAnimationFrame(bind);
        return;
      }
      player = new window.YT.Player(IFRAME_ID, {
        events: {
          onReady: (e) => {
            if (cancelled) return;
            playerRef.current = e.target;
            try {
              e.target.mute();
              e.target.setVolume(0);
            } catch {
              /* ignore */
            }
          },
        },
      });
    };

    loadYouTubeApi().then(() => {
      if (!cancelled) bind();
    });

    return () => {
      cancelled = true;
      playerRef.current = null;
      try {
        player?.destroy?.();
      } catch {
        /* ignore */
      }
    };
  }, [enabled, embedUrl]);

  // Browser gesture unlock — required before unmuted playback
  useEffect(() => {
    if (!enabled) return undefined;

    const unlock = () => {
      if (unlockedRef.current) return;
      unlockedRef.current = true;
      setUnlocked(true);
      const p = playerRef.current;
      if (p) {
        try {
          p.unMute();
        } catch {
          /* ignore */
        }
      }
    };

    window.addEventListener('pointerdown', unlock, { passive: true });
    window.addEventListener('keydown', unlock, { passive: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, [enabled]);

  useFrame(() => {
    _tvWorld.set(pose.position[0], pose.position[1], pose.position[2]);
    const dist = camera.position.distanceTo(_tvWorld);

    // Raycast every 3rd frame — floors/walls block hearing AND visibility
    frameRef.current += 1;
    if (frameRef.current % 3 === 0) {
      occludedRef.current = isTvOccluded(scene, camera.position, _tvWorld);
      setHidden((prev) =>
        prev === occludedRef.current ? prev : occludedRef.current
      );
    }

    const p = playerRef.current;
    if (!p || !unlockedRef.current) return;

    const vol = tvHearingVolume(dist, occludedRef.current, audio);
    if (vol === lastVolRef.current) return;
    lastVolRef.current = vol;

    try {
      if (vol <= 0) {
        p.mute();
        p.setVolume(0);
      } else {
        p.unMute();
        p.setVolume(vol);
      }
    } catch {
      /* player not ready */
    }
  });

  if (!enabled || !embedUrl) return null;

  const title = `${R2AS_TV_MATCH.home} vs ${R2AS_TV_MATCH.away} · ${R2AS_TV_MATCH.note}`;

  return (
    <group position={pose.position} rotation={pose.rotation}>
      <mesh
        position={[0, 0, 0.002]}
        renderOrder={2}
        userData={{ tvAudioIgnore: true }}
      >
        <planeGeometry args={[pose.width, pose.height]} />
        <meshBasicMaterial color="#050508" depthWrite={false} />
      </mesh>

      <Html
        transform
        center
        position={[0, 0, 0.012]}
        distanceFactor={distanceFactor}
        zIndexRange={[40, 0]}
        style={{
          width: CSS_W,
          height: CSS_H,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        <div
          style={{
            width: CSS_W,
            height: CSS_H,
            background: '#050508',
            overflow: 'hidden',
            position: 'relative',
            // Occlusion: DOM overlay must not glow through walls/floors.
            visibility: hidden ? 'hidden' : 'visible',
          }}
        >
          <iframe
            id={IFRAME_ID}
            title={title}
            src={embedUrl}
            width={CSS_W}
            height={CSS_H}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen={false}
            style={{
              border: 'none',
              display: 'block',
              width: CSS_W,
              height: CSS_H,
              pointerEvents: 'none',
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: 16,
              bottom: 14,
              padding: '6px 10px',
              borderRadius: 4,
              background: 'rgba(0,0,0,0.55)',
              color: '#f2f2f2',
              fontFamily: 'JetBrains Mono, SF Mono, Menlo, monospace',
              fontSize: 22,
              letterSpacing: 0.02,
              pointerEvents: 'none',
            }}
          >
            WC26 · ESP 1–0 ARG · highlights
            {!unlocked ? ' · click/key for audio' : ' · proximity audio'}
          </div>
        </div>
      </Html>
    </group>
  );
}

/**
 * True when model geometry (floor/walls) sits between camera and TV.
 */
function isTvOccluded(scene, cameraPos, tvPos) {
  _dir.subVectors(tvPos, cameraPos);
  const dist = _dir.length();
  if (dist < 0.2) return false;
  _dir.multiplyScalar(1 / dist);

  // Start slightly in front of the camera; stop just short of the screen
  _origin.copy(cameraPos).addScaledVector(_dir, 0.08);
  const rayLen = Math.max(0.1, dist - 0.12);

  _raycaster.set(_origin, _dir);
  _raycaster.near = 0;
  _raycaster.far = rayLen;

  const hits = _raycaster.intersectObject(scene, true);
  for (let i = 0; i < hits.length; i += 1) {
    const h = hits[i];
    const obj = h.object;
    if (!obj?.isMesh) continue;
    const ud = obj.userData || {};
    if (ud.labWalkIgnore || ud.tvAudioIgnore || ud.annotateIgnore) continue;
    // SoftPlan model meshes are tagged in LabViewer — floors/walls/slabs block.
    // Skip untagged helpers (gizmo, grid lines, env).
    if (ud.labWalkSurface || ud.featureKind) {
      return true;
    }
  }
  return false;
}

function loadYouTubeApi() {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.YT?.Player) return Promise.resolve(window.YT);

  return new Promise((resolve) => {
    const prior = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      try {
        prior?.();
      } catch {
        /* ignore */
      }
      resolve(window.YT);
    };

    const existing = document.querySelector('script[data-r2as-yt-api]');
    if (!existing) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      tag.async = true;
      tag.dataset.r2asYtApi = '1';
      document.head.appendChild(tag);
    }

    const poll = window.setInterval(() => {
      if (window.YT?.Player) {
        window.clearInterval(poll);
        resolve(window.YT);
      }
    }, 100);
    window.setTimeout(() => window.clearInterval(poll), 15000);
  });
}
